/* eslint-disable max-len */
import { ElementSource, Organization, Story } from "chq";
import getText from "components/editor/getText";
import { findAllGenericElements } from "../../../genericTemplate/utils/findGenericElementByName";
import { SelectedMedia } from "../genericMontageTemplate";

interface ElementUpdate {
  id?: string;
  updates?: Partial<ElementSource>;
}

const getRTValue = (input?: string): string | null => {
  if (!input) return null;
  const match = input.match(/RT=([^@]+)/);
  return match ? match[1] : null;
};

const getModifications = (
  elements: ElementSource[],
  selectedMedia: SelectedMedia[],
  originalElements: ElementSource[],
  organization: Organization,
  originalStoriesEndTime: number
) => {
  const {
    storyElement: storyElements,
    captionElement,
    creatorFirstNameElement,
    creatorLastnameElement,
    creatorNameElement,
    creatorTitleElement,
    creatorDeparmentElement,
    creatorProfessionElement,
    creatorBusinessElement,
    creatorProfileImageElement,
    creatorLocationElement,
    nameAndTitleElement,
    logoElement,
    promptElement,
    trendElement,
    outroElement
  } = findAllGenericElements(elements);

  const sortedStoryElements = [...storyElements].sort((a, b) => a.time - b.time);

  const lastStoryElement = sortedStoryElements.reduce((max, current) => (
    current.time > max.time ? current : max
  ), sortedStoryElements[0]);

  // Create indexed objects for faster lookups
  const sortedStoryElementsMap = sortedStoryElements.reduce((acc, element) => {
    acc[element.id] = element;
    return acc;
  }, {} as Record<string, ElementSource>);

  const originalElementsMap = originalElements.reduce((acc, element) => {
    acc[element.id] = element;
    return acc;
  }, {} as Record<string, ElementSource>);

  const storiesMap: ElementSource = selectedMedia.map((selectedVisual, index) => {
    const storyElement = sortedStoryElements[index];
    if (!storyElement) return {};

    return { [storyElement.id]: selectedVisual };
  }).reduce((acc, obj) => ({ ...acc, ...obj }), {});

  const relativeElements = elements.filter((element: ElementSource) => {
    const elementName = element.name || "";
    const isRelativeElement = elementName.includes("RT=");
    const isIntroElement = elementName.includes("RT=Intro");
    const isOutroElement = elementName.includes("RT=Outro");
    return isRelativeElement && !isIntroElement && !isOutroElement;
  });

  const elementsToDelete: string[] = [];
  const elementUpdates: ElementUpdate[] = [
    ...sortedStoryElements.map(element => ({
      id: element.id,
      updates: storiesMap[element.id] ? {
        name: `${element.name}@@NAME=${storiesMap[element.id].story.creator.name}'s story`
      } : {}
    })),
    ...logoElement.map(element => ({
      id: element.id,
      updates: {
        source: organization.logo.originalUrl,
        name: `${organization.name}'s logo`
      }
    })),
    ...creatorNameElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) {
        elementsToDelete.push(element.id);
        return {};
      }
      const storyElement = sortedStoryElementsMap[storyElementId];
      const originalStoryElement = originalElementsMap[storyElementId];
      return {
        id: element.id,
        updates: {
          text: storiesMap[storyElementId].story.creator.name,
          time: (storyElement?.time ?? 0) + (element.time - (originalStoryElement?.time ?? 0))
        }
      };
    }),
    ...nameAndTitleElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) {
        elementsToDelete.push(element.id);
        return {};
      }
      const storyElement = sortedStoryElementsMap[storyElementId];
      const originalStoryElement = originalElementsMap[storyElementId];
      return {
        id: element.id,
        updates: {
          text: `${storiesMap[storyElementId].story.creator.name}\n${storiesMap[storyElementId].story.creator.title || ""}`,
          time: (storyElement?.time ?? 0) + (element.time - (originalStoryElement?.time ?? 0))
        }
      };
    }),
    ...creatorLocationElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) {
        elementsToDelete.push(element.id);
        return {};
      }
      const storyElement = sortedStoryElementsMap[storyElementId];
      const originalStoryElement = originalElementsMap[storyElementId];
      return {
        id: element.id,
        updates: {
          text: storiesMap[storyElementId].story.creator.location?.name || "",
          time: (storyElement?.time ?? 0) + (element.time - (originalStoryElement?.time ?? 0))
        }
      };
    }),
    ...creatorTitleElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) {
        elementsToDelete.push(element.id);
        return {};
      }
      const storyElement = sortedStoryElementsMap[storyElementId];
      const originalStoryElement = originalElementsMap[storyElementId];
      return {
        id: element.id,
        updates: {
          text: storiesMap[storyElementId].story.creator.title || "",
          time: (storyElement?.time ?? 0) + (element.time - (originalStoryElement?.time ?? 0))
        }
      };
    }),
    ...creatorDeparmentElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) {
        elementsToDelete.push(element.id);
        return {};
      }
      const storyElement = sortedStoryElementsMap[storyElementId];
      const originalStoryElement = originalElementsMap[storyElementId];
      return {
        id: element.id,
        updates: {
          text: storiesMap[storyElementId].story.creator.departments?.[0]?.name || "",
          time: (storyElement?.time ?? 0) + (element.time - (originalStoryElement?.time ?? 0))
        }
      };
    }),
    ...promptElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) {
        elementsToDelete.push(element.id);
        return {};
      }
      const storyElement = sortedStoryElementsMap[storyElementId];
      const originalStoryElement = originalElementsMap[storyElementId];
      return {
        id: element.id,
        updates: {
          text: storiesMap[storyElementId].story.question.question || "",
          time: (storyElement?.time ?? 0) + (element.time - (originalStoryElement?.time ?? 0))
        }
      };
    }),
    ...trendElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) {
        elementsToDelete.push(element.id);
        return {};
      }
      const storyElement = sortedStoryElementsMap[storyElementId];
      const originalStoryElement = originalElementsMap[storyElementId];
      return {
        id: element.id,
        updates: {
          text: storiesMap[storyElementId].story.parentStoryQuestion.question || "",
          time: (storyElement?.time ?? 0) + (element.time - (originalStoryElement?.time ?? 0))
        }
      };
    }),
    ...captionElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) {
        elementsToDelete.push(element.id);
        return {};
      }
      const storyElement = sortedStoryElementsMap[storyElementId];
      const originalStoryElement = originalElementsMap[storyElementId];
      return {
        id: element.id,
        updates: {
          text: getText(storiesMap[storyElementId].story.body || ""),
          time: (storyElement?.time ?? 0) + (element.time - (originalStoryElement?.time ?? 0))
        }
      };
    }),
    ...creatorFirstNameElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) {
        elementsToDelete.push(element.id);
        return {};
      }
      const storyElement = sortedStoryElementsMap[storyElementId];
      const originalStoryElement = originalElementsMap[storyElementId];
      return {
        id: element.id,
        updates: {
          text: storiesMap[storyElementId].story.creator.firstName || storiesMap[storyElementId].story.creator?.name || "",
          time: (storyElement?.time ?? 0) + (element.time - (originalStoryElement?.time ?? 0))
        }
      };
    }),
    ...creatorLastnameElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) {
        elementsToDelete.push(element.id);
        return {};
      }
      const storyElement = sortedStoryElementsMap[storyElementId];
      const originalStoryElement = originalElementsMap[storyElementId];
      return {
        id: element.id,
        updates: {
          text: storiesMap[storyElementId].story.creator.lastName || storiesMap[storyElementId].story.creator?.name || "",
          time: (storyElement?.time ?? 0) + (element.time - (originalStoryElement?.time ?? 0))
        }
      };
    }),
    ...creatorTitleElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) {
        elementsToDelete.push(element.id);
        return {};
      }
      const storyElement = sortedStoryElementsMap[storyElementId];
      const originalStoryElement = originalElementsMap[storyElementId];
      return {
        id: element.id,
        updates: {
          text: storiesMap[storyElementId].story.creator.title || "",
          time: (storyElement?.time ?? 0) + (element.time - (originalStoryElement?.time ?? 0))
        }
      };
    }),
    ...creatorProfessionElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) {
        elementsToDelete.push(element.id);
        return {};
      }
      const storyElement = sortedStoryElementsMap[storyElementId];
      const originalStoryElement = originalElementsMap[storyElementId];
      return {
        id: element.id,
        updates: {
          text: storiesMap[storyElementId].story.creator.departments?.[0]?.name || "",
          time: (storyElement?.time ?? 0) + (element.time - (originalStoryElement?.time ?? 0))
        }
      };
    }),
    ...creatorBusinessElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) {
        elementsToDelete.push(element.id);
        return {};
      }
      const storyElement = sortedStoryElementsMap[storyElementId];
      const originalStoryElement = originalElementsMap[storyElementId];
      return {
        id: element.id,
        updates: {
          text: storiesMap[storyElementId].story.creator.departments?.[0]?.name || "",
          time: (storyElement?.time ?? 0) + (element.time - (originalStoryElement?.time ?? 0))
        }
      };
    }),
    ...creatorProfileImageElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) {
        elementsToDelete.push(element.id);
        return {};
      }
      const storyElement = sortedStoryElementsMap[storyElementId];
      const originalStoryElement = originalElementsMap[storyElementId];
      return {
        id: element.id,
        updates: {
          source: storiesMap[storyElementId].story.creator?.avatar?.thumbUrl || "",
          name: `${storiesMap[storyElementId].story.creator?.name}'s photo`,
          time: (storyElement?.time ?? 0) + (element.time - (originalStoryElement?.time ?? 0))
        }
      };
    }),
    ...outroElement.map(element => {
      const outroDiff = (element?.time ?? 0) - originalStoriesEndTime;
      return {
        id: element.id,
        updates: {
          time: (lastStoryElement?.time ?? 0) + (lastStoryElement?.duration ?? 0) + outroDiff
        }
      };
    }),
    ...relativeElements.map((element: ElementSource) => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) {
        elementsToDelete.push(element.id);
        return {};
      }

      const storyElement = sortedStoryElementsMap[storyElementId];
      const originalStoryElement = originalElementsMap[storyElementId];

      const calculateNewTime = () => (storyElement?.time ?? 0) + (element.time - (originalStoryElement?.time ?? 0));
      const calculateEndTimes = (newTime: number) => ({
        relative: (storyElement.time ?? 0) + (storyElement.duration ?? 0),
        element: newTime + (element.duration ?? 0)
      });

      const newTime = calculateNewTime();
      const { relative: relativeElementEndTime, element: elementEndTime } = calculateEndTimes(newTime);

      return {
        id: element.id,
        updates: elementEndTime > relativeElementEndTime
          ? {
            time: newTime,
            duration: relativeElementEndTime - newTime
          } : { time: newTime }
      };
    }).filter(update => Object.keys(update).length > 0)
  ];

  const elementMappings = elementUpdates
    .filter((update): update is ElementUpdate & { id: string } => Boolean(update.id))
    .reduce((acc, { id, updates }) => {
      acc[id] = { ...acc[id], ...updates };
      return acc;
    }, {} as Record<string, Partial<ElementSource>>);

  return { elementMappings, elementsToDelete };
};

export default getModifications;
